/**
 * Upload Kiosk Icon Use Case
 * Delegates to Asset Management API for icon uploads
 */

import { AssetManagementRepository } from '@/features/asset-management/data/asset-management-repository';
import { AssetUploadFormData, UploadAssetResponse } from '@/features/asset-management/types/asset-types';
import { AssetReference } from '../../core/types/asset-types';
import { fileToBase64, validateIconFile } from '../../core/utils/file-helpers';

/**
 * Request for uploading kiosk icon
 */
export interface UploadKioskIconRequest {
  /** File to upload */
  file: File;

  /** Icon name (e.g., "logo", "chademo") */
  iconName: string;

  /** Operator ID - required for multi-tenant scoping */
  operatorId: number;

  /** Optional machine ID */
  machineId?: string;
}

/**
 * Result of uploading kiosk icon
 */
export interface UploadKioskIconResult {
  /** Success status */
  success: boolean;

  /** Asset reference for the uploaded icon */
  asset?: AssetReference;

  /** Error message if failed */
  error?: string;
}

/**
 * Use case for uploading kiosk icons via Asset Management API
 *
 * Flow:
 * 1. Validate file (type, size)
 * 2. Convert to base64
 * 3. Upload via Asset Management API
 * 4. Return asset reference (assetId, s3Url, fileName)
 */
export class UploadKioskIconUseCase {
  constructor(private readonly assetRepository: AssetManagementRepository) {}

  async execute(request: UploadKioskIconRequest): Promise<UploadKioskIconResult> {
    try {
      // Step 1: Validate file
      const validation = validateIconFile(request.file);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
        };
      }

      // Step 2: Convert file to base64
      const base64Content = await fileToBase64(request.file);

      // Step 3: Prepare upload request for Asset Management API
      const uploadRequest: AssetUploadFormData = {
        applicationType: 'EnergyAndroidKiosk', // Application type for kiosk
        assetType: 'Icon', // Asset type
        name: request.iconName,
        description: `Kiosk ${request.iconName} icon`,
        fileName: request.file.name,
        fileContent: base64Content,
        contentType: request.file.type,
        operatorId: request.operatorId, // Optional, falls back to current operator
        machineId: request.machineId, // Optional
      };

      // Step 4: Upload via Asset Management API
      const response: UploadAssetResponse = await this.assetRepository.uploadAsset(uploadRequest);

      // Step 5: Check response
      if (!response.isSuccess) {
        return {
          success: false,
          error: response.errorMessage || 'Failed to upload icon',
        };
      }

      // Step 6: Build asset reference
      const assetReference: AssetReference = {
        assetId: response.assetId?.toString() || '',
        s3Url: response.currentS3Url || '',
        localFileName: response.currentFileName || request.file.name,
        contentType: request.file.type,
        size: request.file.size,
      };

      return {
        success: true,
        asset: assetReference,
      };
    } catch (error) {
      console.error('Failed to upload kiosk icon:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload icon',
      };
    }
  }
}
